import * as Highcharts from 'highcharts';
import { ReusableFunctions } from './reusable-functions';
import { Router } from '@angular/router';
import { CategoryGroup } from '../interfaces/card-data';

export namespace IncentivesCharts {
    export interface ChartSeries {
        name: string;
        type?: string;
        data: Highcharts.PointOptionsObject[];
        color?: string;
        yAxis?: number;
        tooltip?: any;
    }

    export interface ChartDataPoint {
        x?: number | string;
        y: number;
        color?: string;
        name?: string;
        properties?: any;
        category?: number;
    }

    export interface ChartdataSeries {
        name: string;
        type?: string;
        data: any[];
        legendIndex?: number;
        index?: number;
        color?: string;
    }

    export interface AreaChartDatapoint extends Highcharts.PointOptionsObject {}
    export interface BoxplotData extends Highcharts.PointOptionsObject {}

    export interface AreaChartSeries {
        name: string;
        data: AreaChartDatapoint[];
        color: string;
    }

    export class IncentivesChartOptions {
        constructor(private router: Router, private reusableFunctions: ReusableFunctions) {}
        // TODO: There is a potential to simplyfy all the option methods
        createNewAreaChart(
            yAxisText: string,
            seriesInfo: AreaChartSeries[],
            valueDecimals: number,
            routerinfo: string[],
            categoryGroup: string[] | CategoryGroup[],
            hasDrilldownAccsess: boolean,
            addTitleSuffix: boolean,
            navFromUrl: string = ''
        ): Highcharts.Options {
            Highcharts.setOptions({
                lang: {
                    thousandsSep: ',',
                },
            });

            let _this = this;
            let pointFormat: string = this.getPointFormat(yAxisText);

            return {
                chart: {
                    type: 'area',
                    spacingBottom: 8,
                    spacingLeft: 0,
                    marginRight: 0,
                    spacingRight: 0,
                    spacingTop: 0,
                    events: {
                        load: function () {
                            if (hasDrilldownAccsess) {
                                var xAxis = this.xAxis[0];
                                var xTicks = xAxis.ticks;
                                var yAxis = this.yAxis[0];
                                var points = this.series[0].points;
                                points.forEach((pointInf, i) => {
                                    let point = pointInf as Highcharts.PointOptionsObject;
                                    if (xTicks[i]) {
                                        var label = xTicks[i].label.element;
                                        label.style.cursor = 'pointer';
                                        label.onclick = function (item) {
                                            if (hasDrilldownAccsess) {
                                                let dateInfo = point.description.split('-');
                                                let queryParams = {
                                                    year: dateInfo[0],
                                                    month: dateInfo[1],
                                                    selectedPeriod: dateInfo[1],
                                                    commType: 'All',
                                                };

                                                if (navFromUrl !== '') {
                                                    queryParams['navFromUrl'] = navFromUrl;
                                                }
                                                _this.router.navigate(routerinfo, {
                                                    queryParams,
                                                });
                                            }
                                        };
                                    }
                                });
                            }
                        },
                    },
                },
                navigation: {
                    buttonOptions: {
                        enabled: false,
                    },
                },
                credits: {
                    enabled: false,
                },
                legend: {
                    align: 'left',
                    verticalAlign: 'top',
                },
                title: null,
                xAxis: [
                    {
                        type: 'category',
                        categories: categoryGroup,
                        startOnTick: true,
                        labels: {
                            enabled: true,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                    } as Highcharts.XAxisOptions,
                ],
                yAxis: {
                    title: {
                        text: yAxisText,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },
                    labels: {
                        enabled: true,
                        style: {
                            fontSize: '10px',
                            color: '#666666',
                        },
                    },
                    lineWidth: 1,
                },
                tooltip: {
                    backgroundColor: '#FFFFFF',
                    borderColor: '#223540',
                    borderRadius: 8,
                    style: {
                        color: '#223540',
                        fontFamily: 'Roboto',
                        padding: '8',
                    },
                    shared: true,
                    useHTML: true,
                    headerFormat: '<b>{point.key}</b><br><table>',
                    pointFormat: pointFormat,
                    footerFormat: '</table>',
                    valueDecimals: valueDecimals,
                },
                plotOptions: {
                    area: {
                        stacking: 'normal',
                        lineColor: '#E5E5E5',
                        lineWidth: 1,
                    },
                    series: {
                        marker: {
                            enabled: false,
                            symbol: 'circle',
                        },
                    },
                },
                series: this.formatSeriesInfo(
                    seriesInfo,
                    routerinfo,
                    hasDrilldownAccsess,
                    addTitleSuffix,
                    navFromUrl
                ),
            };
        }

        loadChartOptions(
            chartType: 'bar' | 'boxplot' | 'column',
            unformattedSeriesData: ChartSeries[],
            boxPlotSeriesData: any,
            xAxisText: string,
            yAxis: Highcharts.YAxisOptions[],
            routerinfo: string[],
            categoryGroup: string[] | CategoryGroup[],
            hasDrilldownAccsess: boolean,
            showLegend: boolean,
            navFromUrl: string = ''
        ): Highcharts.Options {
            const _this = this;
            let marginRight = yAxis && yAxis.length > 1 ? 60 : 10;

            return {
                chart: {
                    type: chartType,
                    zooming: {
                        type: 'xy',
                    },
                    spacingBottom: 8,
                    spacingLeft: 0,
                    marginRight: marginRight,
                    spacingRight: 0,
                    spacingTop: 10,
                    events: {
                        load: function () {
                            if (hasDrilldownAccsess) {
                                var xAxis = this.xAxis[0];
                                var xTicks = xAxis.ticks;
                                var yAxis = this.yAxis[0];
                                var points = this.series[0].points;
                                points.forEach((pointInf, i) => {
                                    let point = pointInf as Highcharts.PointOptionsObject;
                                    if (xTicks[i]) {
                                        var label = xTicks[i].label.element;
                                        label.style.cursor = 'pointer';
                                        label.onclick = function (item) {
                                            if (hasDrilldownAccsess) {
                                                let dateInfo = point.description.split('-');
                                                let queryParams = {
                                                    year: dateInfo[0],
                                                    month: dateInfo[1],
                                                    selectedPeriod: dateInfo[1],
                                                    commType: 'All',
                                                };

                                                if (navFromUrl !== '') {
                                                    queryParams['navFromUrl'] = navFromUrl;
                                                }

                                                _this.router.navigate(routerinfo, {
                                                    queryParams,
                                                });
                                            }
                                        };
                                    }
                                });
                            }
                        },
                    },
                },
                exporting: {
                    enabled: false,
                },
                title: null,
                legend: {
                    enabled: showLegend,
                    align: 'left',
                    verticalAlign: 'top',
                },
                credits: {
                    enabled: false,
                },
                series: unformattedSeriesData
                    ? _this.formatSeriesData(
                          unformattedSeriesData,
                          routerinfo,
                          hasDrilldownAccsess,
                          navFromUrl
                      )
                    : _this.setBoxPlotSeriesData(
                          boxPlotSeriesData,
                          routerinfo,
                          hasDrilldownAccsess,
                          navFromUrl
                      ),
                xAxis: [
                    {
                        type: 'category',
                        categories: categoryGroup,
                        tickLength: 1,
                        tickInterval: 1,
                        pointInterval: 1,
                        labels: {
                            enabled: true,
                            rotation: 0,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                        title: {
                            text: xAxisText,
                            style: {
                                fontSize: '10px',
                                color: '#666666',
                            },
                        },
                    } as Highcharts.XAxisOptions,
                ],

                tooltip: {
                    backgroundColor: '#FFFFFF',
                    borderColor: '#223540',
                    borderRadius: 8,
                    style: {
                        color: '#223540',
                        fontFamily: 'Roboto',
                        padding: '8',
                    },
                    shared: true,
                    useHTML: true,
                    headerFormat: '<b>{point.key}</b><br><table>',
                    pointFormatter: function (point) {
                        let tooltip = '';
                        if (chartType === 'boxplot') {
                            tooltip = `               
                                <tr>
                                  <td>Maximum: </td>
                                  <td style="text-align: right">${this.high.toFixed(3)} BPS</td>
                                </tr>
                                <tr>
                                  <td>Upper Quartile: </td>
                                  <td style="text-align: right">${this.q3.toFixed(3)} BPS</td>
                                </tr>
                                <tr>
                                  <td>Median: </td>
                                  <td style="text-align: right">${this.median.toFixed(3)} BPS</td>
                                </tr>
                                <tr>
                                  <td>Lower Quartile: </td>
                                  <td style="text-align: right">${this.q1.toFixed(3)} BPS</td>
                                </tr>
                                <tr>
                                  <td>Minimum: </td>
                                  <td style="text-align: right">${this.low.toFixed(3)} BPS</td>
                                </tr>`;
                        } else {
                            let value = '';
                            if (this.custom && this.custom.dataType && this.y) {
                                if (this.custom.dataType === 'money') {
                                    value = _this.reusableFunctions.formatMoney(this.y);
                                } else if (this.custom.dataType.toLowerCase() === 'bps') {
                                    value = this.y.toFixed(3) + ' BPS';
                                }
                            }
                            tooltip = `<tr><td style="color: ${this.color}"><b>${this.series.name}:</b> </td>
                                <td style="text-align: right">${value}</td></tr>`;
                        }
                        return tooltip;
                    },
                    footerFormat: '</table>',
                } as Highcharts.TooltipOptions,

                yAxis: yAxis,
                plotOptions: {
                    column: {
                        states: {
                            hover: {
                                brightness: -0.3,
                            },
                        },
                    },
                    series: {
                        marker: {
                            enabled: false,
                            states: {
                                hover: {
                                    radius: 4,
                                    enabled: true,
                                    radiusPlus: 0,
                                    lineWidthPlus: 0,
                                    lineWidth: 0,
                                },
                            },
                        },
                        maxPointWidth: 50,
                        groupPadding: 0,
                        lineWidth: 3,
                        gridLineWidth: 0,
                        minorGridLineWidth: 0,
                    } as Highcharts.PlotSeriesOptions,
                },
            };
        }

        formatSeriesData(
            seriesData: ChartSeries[],
            routerinfo: string[],
            hasDrilldownAccsess: boolean,
            navFromUrl: string
        ): (Highcharts.SeriesColumnOptions | Highcharts.SeriesSplineOptions)[] {
            let chartSeriesInfo: (
                | Highcharts.SeriesColumnOptions
                | Highcharts.SeriesSplineOptions
            )[] = [];
            seriesData.forEach((series) => {
                chartSeriesInfo.push(
                    this.formatThisSeries(series, routerinfo, hasDrilldownAccsess, navFromUrl)
                );
            });
            return chartSeriesInfo;
        }

        formatThisSeries(
            series: ChartSeries,
            routerinfo: string[],
            hasDrilldownAccsess: boolean,
            navFromUrl: string
        ): Highcharts.SeriesColumnOptions | Highcharts.SeriesSplineOptions {
            switch (series.type) {
                case 'column':
                    return this.getSeriesColumnOptions(
                        series,
                        routerinfo,
                        hasDrilldownAccsess,
                        navFromUrl
                    );
                case 'spline':
                    return this.getSeriesSplineOptions(
                        series,
                        routerinfo,
                        hasDrilldownAccsess,
                        navFromUrl
                    );
                default:
                    throw new Error('Invalid series type.');
            }
        }

        getSeriesColumnOptions(
            series: ChartSeries,
            routerinfo: string[],
            hasDrilldownAccsess: boolean,
            navFromUrl: string
        ): Highcharts.SeriesColumnOptions {
            const _this = this;
            return {
                name: series.name,
                type: 'column',
                groupPadding: 0,
                data: series.data,
                color: series.color,
                cursor: hasDrilldownAccsess ? 'pointer' : 'default',
                yAxis: series.yAxis,
                tooltip: series.tooltip,
                point: {
                    events: {
                        click: function (dataPoint) {
                            if (hasDrilldownAccsess) {
                                let dateInfo = (
                                    dataPoint.point as AreaChartDatapoint
                                ).description.split('-');
                                let queryParams = {
                                    year: dateInfo[0],
                                    month: dateInfo[1],
                                    selectedPeriod: dateInfo[1],
                                    //titleSuffix: ': ' + series.name,
                                };

                                if (navFromUrl !== '') {
                                    queryParams['navFromUrl'] = navFromUrl;
                                }
                                _this.router.navigate(routerinfo, {
                                    queryParams,
                                });
                            }
                        },
                    },
                },
            };
        }

        setBoxPlotSeriesData(
            boxPlotSeriesData: Highcharts.SeriesBoxplotOptions[],
            routerinfo: string[],
            hasDrilldownAccsess,
            navFromUrl: string
        ): Highcharts.SeriesBoxplotOptions[] {
            const _this = this;

            boxPlotSeriesData.forEach((boxSeries) => {
                boxSeries.cursor = hasDrilldownAccsess ? 'pointer' : 'default';
                boxSeries.point = {
                    events: {
                        click: function (dataPoint) {
                            if (hasDrilldownAccsess) {
                                let dateInfo = (
                                    dataPoint.point as AreaChartDatapoint
                                ).description.split('-');
                                let queryParams = {
                                    year: dateInfo[0],
                                    month: dateInfo[1],
                                    selectedPeriod: dateInfo[1],
                                };

                                if (navFromUrl !== '') {
                                    queryParams['navFromUrl'] = navFromUrl;
                                }

                                _this.router.navigate(routerinfo, {
                                    queryParams,
                                });
                            }
                        },
                    },
                };
            });
            return boxPlotSeriesData;
        }

        getSeriesSplineOptions(
            series: ChartSeries,
            routerinfo: string[],
            hasDrilldownAccsess: boolean,
            navFromUrl: string
        ): Highcharts.SeriesSplineOptions {
            const _this = this;
            return {
                name: series.name,
                type: 'spline',
                data: series.data,
                color: series.color,
                cursor: hasDrilldownAccsess ? 'pointer' : 'default',
                yAxis: series.yAxis,
                tooltip: series.tooltip,
                point: {
                    events: {
                        click: function (dataPoint) {
                            if (hasDrilldownAccsess) {
                                let dateInfo = (
                                    dataPoint.point as AreaChartDatapoint
                                ).description.split('-');

                                let queryParams = {
                                    year: dateInfo[0],
                                    month: dateInfo[1],
                                    selectedPeriod: dateInfo[1],
                                    //titleSuffix: ': ' + series.name,
                                };

                                if (navFromUrl !== '') {
                                    queryParams['navFromUrl'] = navFromUrl;
                                }
                                _this.router.navigate(routerinfo, {
                                    queryParams,
                                });
                            }
                        },
                    },
                },
            };
        }

        formatSeriesInfo(
            seriesInfo: AreaChartSeries[],
            routerinfo: string[],
            hasDrilldownAccsess: boolean,
            addTitleSuffix: boolean,
            navFromUrl: string
        ) {
            let series: Highcharts.SeriesAreaOptions[] = [];
            const _this = this;
            seriesInfo.forEach((category, index) => {
                series.push({
                    type: 'area',
                    name: category.name,
                    data: category.data,
                    color: category.color,
                    fillColor: category.color,
                    fillOpacity: 1,
                    lineColor: category.color,
                    index: seriesInfo.length - index - 1,
                    legendIndex: index,
                    cursor: hasDrilldownAccsess ? 'pointer' : 'default',
                    point: {
                        events: {
                            click: function (dataPointEvent) {
                                if (hasDrilldownAccsess) {
                                    let dataPoint = dataPointEvent.point as AreaChartDatapoint;
                                    let dateInfo = dataPoint.description.split('-');
                                    let queryParams = {
                                        year: dateInfo[0],
                                        month: dateInfo[1],
                                        selectedPeriod: dateInfo[1],
                                        //TODO: Remove 'slice' once the sproc is ready to get the whole word as type
                                        commType: category.name.slice(0, 1),
                                    };

                                    if (addTitleSuffix) {
                                        queryParams['titleSuffix'] = ': ' + category.name;
                                    }

                                    if (navFromUrl !== '') {
                                        queryParams['navFromUrl'] = navFromUrl;
                                    }

                                    _this.router.navigate(routerinfo, {
                                        queryParams: queryParams,
                                    });
                                }
                            },
                        },
                    },
                });
            });
            return series;
        }

        navigateTo(dataPointEvent, routerinfo: string[], category: AreaChartSeries) {
            let dataPoint = dataPointEvent.point as AreaChartDatapoint;
            let dateInfo = dataPoint.description.split('-');
            this.router.navigate(routerinfo, {
                queryParams: {
                    year: dateInfo[0],
                    month: dateInfo[1],
                    selectedPeriod: dateInfo[1],
                    titleSuffix: ': ' + category.name,
                    //TODO: Remove 'slice' once the sproc is ready to get the whole word as type
                    commType: category.name.slice(0, 1),
                },
            });
        }

        getPointFormat(yAxisText: string): string {
            if (yAxisText.toLowerCase().includes('bps')) {
                return (
                    '<tr><td style="color: {series.color}"><b>{series.name}:</b> </td>' +
                    '<td style="text-align: right">{point.y} BPS</td></tr>'
                );
            } else if (yAxisText.toLowerCase().includes('dollar')) {
                return (
                    '<tr><td style="color: {series.color}"><b>{series.name}:</b> </td>' +
                    '<td style="text-align: right">${point.y}</td></tr>'
                );
            }
        }
    }
}
